import { useState, useCallback, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import useMountStatus from '../../../../hooks/useMountStatus';

import { EMPTY_FIELDS } from '../../../../utils/constants';
import { getOrganisationUsers, uploadReport } from "../../../../utils/api";
import { UploadIcon, DocumentIcon, TagIcon } from "@heroicons/react/solid";

import Alert, { ALERT_TYPE } from "../../../Auth/Alert";
import DashboardContainer from "../../../SharedComponents/DashboardContainer";
import Input from '../../../Auth/Input';
import Button from "../../../SharedComponents/Button";
import Tag from "../../../SharedComponents/Tag";

function UploadReport() {
    const [user, setUser] = useState("");
    const [tagInput, setTagInput] = useState("");
    const [reportFile, setReportFile] = useState("");
    const [reportName, setReportName] = useState("");
    const [reportTags, setReportTags] = useState([]);
    const [organisationUsers, setOrganisationUsers] = useState([]);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [alert, setAlert] = useState('');
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const [usersLoading, setUsersLoading] = useState(false);

    const unmounted = useMountStatus();
    const navigate = useNavigate();

    const isInputValid = () => {
        if (user === "" || reportFile === "") {
            setAlert(EMPTY_FIELDS);
            return false
        }
        return true
    }

    const handleSuccessfulUploadReport = async (documentIdentifier) => {
        navigate("/reports", {
            state: {

                uploadedReport: documentIdentifier
            }
        });
    }

    const handleUploadReport = async () => {
        if (isInputValid()) {
            try {
                setSubmitLoading(true);
                const documentIdentifier = await uploadReport(user.emailAddress, reportFile, reportName, reportTags);
                await handleSuccessfulUploadReport(documentIdentifier);
                setSubmitLoading(false)
            } catch (error) {
                setSubmitLoading(false);

                setAlertType(ALERT_TYPE.ERROR);
                setAlert(error.message);
            }
        }
    }

    const handleTagsKeyPress = (key) => {
        if (key === 'Enter' && tagInput.length > 1) {
            setReportTags(tags => [...new Set([...tags, tagInput.toLowerCase().replace(/\W/g, '')])]);
            setTagInput("");
        }
    }

    const deleteTag = (tagToDelete) => {
        setReportTags(tags => tags.filter(tag => tag !== tagToDelete));
    }

    const fetchOrganisationUsers = useCallback(async () => {
        setUsersLoading(true);
        try {
            const organisationUsers = await getOrganisationUsers();
            if (unmounted) return;
            setOrganisationUsers(organisationUsers);
        } catch (err) {
            if (unmounted) return;
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setUsersLoading(false);
    }, [unmounted]);

    useEffect(() => {
        fetchOrganisationUsers().then();
    }, [fetchOrganisationUsers]);

    return (
        <DashboardContainer title={"Upload Report"} titleIcon={<UploadIcon className="block h-5 w-5 mr-2"/>}>
            <div className="grid grid-flow-row gap-4">
                <div className="md:col-span-1">
                    <div>
                        <h3 className="text-md font-medium leading-6 text-indigo-900">
                            Report details
                        </h3>
                        <p className="text-sm text-gray-600">
                            Please complete the form below to upload a report
                        </p>
                    </div>
                </div>
                <div className="md:col-span-2">
                    <form action="#" method="POST">
                        <Alert
                            text={alert}
                            type={alertType}
                            active={alert}
                            close={() => setAlert("")}
                        />
                        <div className="shadow-sm overflow-hidden rounded-md">
                            <div className="grid grid-flow-row gap-4 md:gap-6 p-4 bg-white">
                                <div className={"grid grid-flow-row md:grid-cols-6 gap-4 md:gap-6"}>
                                    <div className={
                                        "h-max col-span-3 grid grid-flow-row gap-4 md:gap-6 lg:col-span-3 " +
                                        "sm:col-span-6"
                                    }>
                                        <div>
                                            <label
                                                htmlFor="organisationUsers"
                                                className="block text-sm font-medium text-indigo-900">
                                                Candidate/User
                                            </label>
                                            <select
                                                id="organisationUsers"
                                                name="organisationUsers"
                                                disabled={usersLoading}
                                                className={
                                                    "relative w-full bg-blue-gray-50 border border-gray-300 mt-2 " +
                                                    "rounded-md shadow-sm pl-3 text-left focus:outline-none " +
                                                    "focus:ring-1 focus:ring-primary-600 focus:border-primary-600 sm:text-sm"
                                                }
                                                onChange={
                                                    e => setUser(organisationUsers
                                                        .filter(organisationUser =>
                                                            (organisationUser.emailAddress === e.target.value))[0])
                                                }>
                                                {usersLoading ?
                                                    <option value="" className={"italic"}>
                                                        Loading organisation users..
                                                    </option>
                                                    :
                                                    <option value="no_access">Please select</option>
                                                }
                                                {organisationUsers.map((user, index) => (
                                                    <option key={index} value={user.emailAddress}
                                                            className={"mt-1"}>
                                                        {user.name}: {user.emailAddress}
                                                    </option>
                                                ))
                                                }
                                            </select>
                                        </div>
                                        <Input
                                            id="reportDocument"
                                            type="file"
                                            name="File"
                                            placeholder="Choose a file"
                                            onChange={setReportFile}
                                            Icon={<DocumentIcon className="h-4 w-4 text-gray-500"/>}
                                        />
                                    </div>
                                    <div className={
                                        "shrink-0 col-span-3 grid grid-flow-row gap-4 md:gap-6 lg:col-span-3 " +
                                        "sm:col-span-6"
                                    }>
                                        <Input
                                            optional
                                            id="reportName"
                                            type="text"
                                            name="Report Name"
                                            placeholder="Enter report name"
                                            onChange={setReportName}
                                        />
                                        <Input
                                            optional
                                            id="tags"
                                            type="text"
                                            name="Tags"
                                            placeholder="Enter tag and press enter key"
                                            value={tagInput}
                                            onChange={setTagInput}
                                            Icon={<TagIcon className="h-4 w-4 text-gray-500"/>}
                                            onKeyPress={handleTagsKeyPress}
                                        />
                                        <div className="flex flex-wrap">
                                            {reportTags.map(tag => <Tag key={tag} text={tag} extraStyling="mr-2"
                                                                        onDelete={() => deleteTag(tag)}/>)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={
                                "p-4 bg-gradient-to-l from-primary-50 to-secondary-50 flex items-center justify-end " +
                                "text-right"
                            }>
                                <Button
                                    text="Upload Report"
                                    icon={<UploadIcon className="block h-4 w-4 mr-2"/>}
                                    iconPositionLeft
                                    onClick={handleUploadReport}
                                    loading={submitLoading}
                                    extraStyling={
                                        "text-white focus:ring-inset focus:ring-1 bg-secondary-500 " +
                                        "hover:bg-secondary-600 focus:outline-none focus:ring-secondary-500"
                                    }
                                />
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </DashboardContainer>
    );
}

export default UploadReport;
